import {HTTPClient as HTTPClientCore, IRequestConfig} from "@fanhubmedia/fe-common-utils";
import axios, {AxiosError} from "axios";
import {isObject, set} from "lodash";
import {CANCEL} from "redux-saga";
import {API_URL, FANTASY_API_URL, JSON_URL} from "modules/constants";
import {
	IForgotPasswordPayload,
	IGetSettingsResponse,
	IRecoveryPasswordPayload,
	ITeamsResponse,
	ILoginPayload,
	IRegisterPayload,
	IValidateTokenPayload,
	IAvatarsResponse,
	IAchievementsResponse,
	TCountries,
	IUpdateUserPayload,
	IChangePasswordPayload,
	IChangeAvatarPayload,
	IUpdateTeamName,
} from "modules/actions";
import {IApiResponse} from "modules/types";
import {ApiError, getLang} from "modules/utils";
import {IAchievement, IHelp, IUser} from "modules/reducers";

class HTTPClient extends HTTPClientCore {
	/**
	 * Overridden method adds CancelToken symbol, that allow redux-saga'
	 * "takeLatest" function to cancel any requests automatically.
	 * http://fe-common-utils.s3-website-eu-west-1.amazonaws.com/classes/httpclient.html
	 */
	public makeRequest<T>(config: IRequestConfig): Promise<T> {
		const source = axios.CancelToken.source();

		const request = super.makeRequest<T>({
			...config,
			cancelToken: source.token,
		});

		return set<Promise<T>>(request, CANCEL, () => source.cancel());
	}
}

const onCatchNetworkError = ({response, message = "Network error"}: AxiosError<IApiResponse>) => {
	const data = response?.data;
	const error = isObject(data)
		? {
				...data,
				errors: data.errors.map((e) => new ApiError(e.message, response?.status)),
		  }
		: {
				errors: [new ApiError(message, response?.status)],
		  };

	return Promise.reject(error).catch((err) => ApiError.CHECK(err as IApiResponse));
};

export const APIClient = new HTTPClient({
	baseURL: API_URL,
	withCredentials: true,
	onCatchNetworkError,
});

export const FantasyAPIClient = new HTTPClient({
	baseURL: FANTASY_API_URL,
	withCredentials: true,
	onCatchNetworkError,
});

const JSONClient = new HTTPClient({
	baseURL: JSON_URL,
	onCatchNetworkError,
});

type TUserResponse = IApiResponse<{user: IUser}>;
type TAchievementsResponse = IApiResponse<{achievements: IAchievement[]}>;
type THelpsResponse = {prizes: IHelp[]; terms: IHelp[]; how_to_play: IHelp[]};

export const Api = {
	JSON: {
		settings: () => JSONClient.get<IGetSettingsResponse>("settings.json"),
		teams: () => JSONClient.get<ITeamsResponse>("favoriteTeams.json"),
		faqs: () => JSONClient.get<IHelp[]>(`gamezone/${getLang()}/faq.json`),
		helps: () => JSONClient.get<THelpsResponse>(`gamezone/${getLang()}/help_pages.json`),
		avatars: () => JSONClient.get<IAvatarsResponse>("avatars.json"),
		achievements: () => JSONClient.get<IAchievementsResponse>("achievements.json"),
		countries: () => JSONClient.get<TCountries>("countries.json"),
	},
	Auth: {
		login: (params: ILoginPayload) => APIClient.post<TUserResponse>("auth/login", params),
		register: (params: IRegisterPayload) =>
			APIClient.post<TUserResponse>("auth/register", params),
		forgotPassword: (params: IForgotPasswordPayload) =>
			APIClient.post<IApiResponse>("auth/password_reset/request", params),
		recoveryPassword: (params: IRecoveryPasswordPayload) =>
			APIClient.post<IApiResponse>("auth/password_reset", params),
		validateToken: (params: IValidateTokenPayload) =>
			APIClient.get<IApiResponse>(`auth/password_reset/validate_token/${params.token}`),
		logout: () => APIClient.post("auth/logout"),
	},
	User: {
		get: () => APIClient.get<TUserResponse>("user"),
		update: (payload: IUpdateUserPayload) =>
			APIClient.post<TUserResponse>("user/update", payload),
		changePassword: (payload: IChangePasswordPayload) =>
			APIClient.post<TUserResponse>("user/update", payload),
		changeAvatar: (payload: IChangeAvatarPayload) =>
			APIClient.post<TUserResponse>("user/avatar", payload),
	},
	Achievements: {
		get: () => APIClient.get<TAchievementsResponse>("achievements"),
	},
	Fantasy: {
		updateTeamName: (payload: IUpdateTeamName) =>
			FantasyAPIClient.post(`${getLang()}/fantasy/team/change_name`, payload),
	},
};

export * from "./ApiError";

export default Api;
